import React from 'react'
import { Button, Icon } from 'react-bulma-components'
import { Link } from 'react-router-dom'
import { Mission } from '../../../api/missions'
import {
  DuplicateIcon,
  EmployerServiceIcon,
  InterimAgencyIcon,
  JobTitleIcon,
  MissionCancelledIcon,
  MissionValidatedIcon,
} from '../../icons'
import PageTitle from '../../pages/page-title'
import Protected, { isAuthorizedFor } from '../../protected/protected'
import { getUserLabel } from '../../../utils/users'
import ButtonWithTooltip from '../../button-with-tooltip/button-with-tooltip'
import { useModal } from '../../modal'
import MissionCancelConfirmation from '../modals/cancel-confirmation'
import MissionValidateConfirmation from '../modals/validate-mission-confirmation'
import { MissionIsCancelledMessage } from '../mission-messages'

interface MissionPageTitleProps {
  mission?: Mission
  organizationType: 'interimAgency' | 'employer'
  children?: React.ReactNode
}

const MissionPageTitle: React.FC<MissionPageTitleProps> = ({
  mission,
  organizationType,
  children,
}) => {
  if (!mission) {
    return <PageTitle title="Nouvelle Mission Interne" />
  } else
    return (
      <PageTitle
        title={getUserLabel(mission.user)}
        subTitle={
          <>
            <Icon.Text mr={5} data-test="mission-details-service-name">
              <Icon mr={1}>
                <EmployerServiceIcon />
              </Icon>
              {mission.service.name}
            </Icon.Text>
            <Icon.Text mr={5} data-test="mission-details-job-title-name">
              <Icon mr={1}>
                <JobTitleIcon />
              </Icon>
              {mission.jobTitle.name}
            </Icon.Text>
            <Icon.Text mr={5} data-test="mission-details-interim-agency-name">
              <Icon mr={1}>
                <InterimAgencyIcon />
              </Icon>
              {mission?.[organizationType]?.name}
            </Icon.Text>
          </>
        }
      >
        {children}
      </PageTitle>
    )
}

export default MissionPageTitle

export const EditActions: React.FC<Pick<MissionPageTitleProps, 'mission'>> = ({ mission }) => {
  const cancelModal = useModal()
  const validateModal = useModal()

  if (!mission) return null

  const firstCancellableWorkPeriod = mission.workPeriods.find(
    workPeriod => workPeriod.__actions.canBeCancelled,
  )
  const missionIsCancellable = firstCancellableWorkPeriod !== undefined
  const missionCanBeValidated = mission.workPeriods.every(w => w.__actions.canBeValidated)

  return (
    <>
      {/* Validate entire mission */}
      {isAuthorizedFor(['employerMember'], { resource: 'missions', name: 'validate' }) && (
        <Protected roles={['employerMember']}>
          {!mission.isValidated && (
            <>
              <ButtonWithTooltip
                type="button"
                color={'primary'}
                size="small"
                onClick={() => validateModal.setIsDisplayed(true)}
                disabled={!missionCanBeValidated}
                tooltip={{
                  text: 'Cette Mission ne peut être validée',
                  show: !missionCanBeValidated,
                }}
                data-test="validate-mission"
              >
                <Icon>
                  <MissionValidatedIcon />
                </Icon>
                <span>Valider la mission</span>
              </ButtonWithTooltip>
              {missionCanBeValidated && (
                <MissionValidateConfirmation
                  show={validateModal.isDisplayed}
                  hide={() => validateModal.setIsDisplayed(false)}
                  mission={mission}
                />
              )}
            </>
          )}
        </Protected>
      )}

      {/* Interrupt mission */}
      {isAuthorizedFor(['employerMember', 'interimAgencyMember'], {
        resource: 'missions',
        name: 'cancel',
      }) && (
        <Protected roles={['interimAgencyMember', 'employerMember']}>
          {mission.isCancelled ? (
            <MissionIsCancelledMessage mission={mission} type="tag" />
          ) : (
            !mission.isValidated && (
              <>
                <ButtonWithTooltip
                  type="button"
                  color={'danger'}
                  size="small"
                  onClick={() => firstCancellableWorkPeriod && cancelModal.setIsDisplayed(true)}
                  disabled={!firstCancellableWorkPeriod}
                  tooltip={{
                    text: "Cette Mission n'est pas interruptible",
                    show: !missionIsCancellable,
                  }}
                  data-test="cancel-mission"
                >
                  <Icon>
                    <MissionCancelledIcon />
                  </Icon>
                  <span>Interrompre</span>
                </ButtonWithTooltip>
                {missionIsCancellable && firstCancellableWorkPeriod && (
                  <MissionCancelConfirmation
                    show={cancelModal.isDisplayed}
                    hide={() => cancelModal.setIsDisplayed(false)}
                    mission={mission}
                    workPeriod={firstCancellableWorkPeriod}
                  />
                )}
              </>
            )
          )}
        </Protected>
      )}

      {/* Duplicate as new workers request */}
      {mission.interimAgency ? (
        <Protected
          roles={['employerMember']}
          action={{ resource: 'workersRequests', name: 'create' }}
        >
          <Button
            color={'link'}
            outlined
            renderAs={Link}
            size="small"
            to={`/workers-requests/new?missionTemplate=${mission._id}`}
          >
            <Icon mr={1}>
              <DuplicateIcon />
            </Icon>
            Utiliser pour une nouvelle Demande
          </Button>
        </Protected>
      ) : null}
    </>
  )
}
